import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import {
  Button, FormGroup, Intent, Alert, InputGroup, Spinner
} from "@blueprintjs/core";
import FormDrawer from "components/FormDrawer";
import { FormSection, FieldError } from "components/FormBits";

// Template descriptions are HTML (from the rich text editor); show them as text.
const plainText = (html) => {
  if (!html) {
    return '';
  }
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    doc.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((block) => block.append('\n'));
    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }
  return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const styles = {
  list: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: 6,
    maxHeight: 320,
    overflowY: 'auto',
    margin: '0 0 6px',
    padding: 2,
  },
  option: (selected) => ({
    display: 'block',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '10px 12px',
    border: `1px solid ${selected ? 'var(--fd-accent)' : 'var(--fd-line)'}`,
    borderRadius: 10,
    background: selected ? 'var(--fd-accent-tint)' : 'var(--fd-surface)',
    color: 'var(--fd-ink)',
    textAlign: 'left',
    cursor: 'pointer',
  }),
  name: { display: 'block', fontSize: 14, fontWeight: 800 },
  snippet: {
    display: 'block',
    marginTop: 2,
    overflow: 'hidden',
    color: 'var(--fd-muted)',
    fontSize: 13,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  status: { margin: '4px 0 12px', color: 'var(--fd-muted)', fontSize: 13 },
  preview: {
    maxHeight: 260,
    overflowY: 'auto',
    padding: '10px 12px',
    borderRadius: 10,
    background: 'var(--fd-field)',
    color: 'var(--fd-text)',
    fontSize: 13,
    lineHeight: '19px',
    whiteSpace: 'pre-wrap',
  },
};

@inject('scopeOfWorkTemplateStore','store') @observer
class SelectScopeOfWorkModal extends Component {
  // Index of the chosen template in the store's list (its description is what's used).
  @observable selectedIndex = null;
  @observable query = '';
  @observable error = null;
  @observable isConfirmDialogVisible = false;

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.selectedIndex = null;
      this.query = '';
      this.error = null;
    }
  }

  loadScopeOfWorkTemplates() {
    this.props.scopeOfWorkTemplateStore.loadScopeOfWorkTemplates();
    const search = document.getElementById('scope-of-work-search');
    if (search) {
      search.focus();
    }
  }

  get selectedScopeOfWork() {
    const { objects } = this.props.scopeOfWorkTemplateStore.scopeOfWorkTemplates;
    const template = this.selectedIndex === null ? null : objects[this.selectedIndex];
    return template ? template?.attributes?.description : "";
  }

  handleClosDailog() {
    this.props.onClose()
  }

  selectScopeOfWork() {
    if(this.selectedScopeOfWork === "" || this.selectedScopeOfWork === null || this.selectedScopeOfWork === undefined) {
      this.error = this.selectedIndex === null ? 'Choose a template to use.' : 'This template has no text to use. Choose another one.';
      return;
    }

    if (this.props.warningRequired) {
      this.isConfirmDialogVisible = true;
    } else {
      this.props.onSuccess(this.selectedScopeOfWork);
    }
  }

  handleConfirmedDialogCancel() {
    this.isConfirmDialogVisible = false;
  }

  handleOnConfirm() {
    this.props.onSuccess(this.selectedScopeOfWork)
    this.isConfirmDialogVisible = false;
  }

  render() {
    let {
      scopeOfWorkTemplateStore: {
        scopeOfWorkTemplates: {
          isLoading,
          objects
        }
      },
    } = this.props;
    const close = this.handleClosDailog.bind(this);
    const query = this.query.trim().toLowerCase();
    const templates = objects
      .map((template, index) => ({
        index,
        name: template?.attributes?.name || 'Untitled template',
        text: plainText(template?.attributes?.description),
      }))
      .filter((template) => !query || template.name.toLowerCase().includes(query) || template.text.toLowerCase().includes(query));
    const selected = this.selectedIndex === null ? null : objects[this.selectedIndex];
    const selectedText = selected ? plainText(selected?.attributes?.description) : '';
    return(
      <FormDrawer
        isOpen={this.props.isOpen}
        title="Choose a scope of work template"
        icon="clipboard"
        onOpened={this.loadScopeOfWorkTemplates.bind(this)}
        onClose={close}
        footer={
          <>
            <Button text="Cancel" onClick={close} />
            <Button
              intent={Intent.PRIMARY}
              icon="tick"
              text={this.props.warningRequired ? "Replace scope of work" : "Use template"}
              onClick={this.selectScopeOfWork.bind(this)}
            />
          </>
        }
      >
        <FormSection
          title="Template"
          hint={this.props.warningRequired
            ? "Its text replaces the scope of work already on this estimate."
            : "Its text becomes the estimate's scope of work. You can edit it afterwards."}
        >
          <FormGroup label="Find a template" labelFor="scope-of-work-search">
            <InputGroup
              id="scope-of-work-search"
              leftIcon="search"
              placeholder="e.g. furnace install"
              value={this.query}
              onChange={(event) => { this.query = event.target.value; }}
              disabled={isLoading && objects.length === 0}
            />
          </FormGroup>

          <div className={this.error ? 'has-error' : undefined}>
            <span className="fb-label" id="scope-of-work-template-label">
              Template<span className="fb-required" aria-hidden="true"> *</span>
            </span>
            {isLoading && objects.length === 0 ? (
              <p style={styles.status}><Spinner size={14} tagName="span" /> Loading templates…</p>
            ) : templates.length === 0 ? (
              <p style={styles.status}>
                {objects.length === 0 ? 'No scope of work templates yet.' : `No templates match “${this.query.trim()}”.`}
              </p>
            ) : (
              <div style={styles.list} role="radiogroup" aria-labelledby="scope-of-work-template-label">
                {templates.map((template) => {
                  const isSelected = template.index === this.selectedIndex;
                  return (
                    <button
                      key={template.index}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className="fb-choice__option"
                      style={styles.option(isSelected)}
                      onClick={() => {
                        this.selectedIndex = template.index;
                        this.error = null;
                      }}
                      onDoubleClick={() => {
                        this.selectedIndex = template.index;
                        this.selectScopeOfWork();
                      }}
                    >
                      <span style={styles.name}>{template.name}</span>
                      {template.text && <span style={styles.snippet}>{template.text.replace(/\s+/g, ' ')}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <FieldError>{this.error}</FieldError>
          </div>
        </FormSection>

        {selected && (
          <FormSection title="Preview" hint={selected?.attributes?.name}>
            <div style={styles.preview}>{selectedText || 'This template has no text.'}</div>
          </FormSection>
        )}

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Replace it"
          icon="high-priority"
          intent={Intent.DANGER}
          isOpen={this.isConfirmDialogVisible}
          onCancel={this.handleConfirmedDialogCancel.bind(this)}
          onConfirm={this.handleOnConfirm.bind(this)}
        >
          <p>
            The scope of work already on this estimate will be replaced by this template. Continue?
          </p>
        </Alert>
      </FormDrawer>
    )
  }
}

export default SelectScopeOfWorkModal;
